<template>
  <div
    class="border rounded-lg"
    v-if="!isEmpty(result)"
    :class="mode === 'dark' ? 'border-dark-50' : ''"
  >
    <div
      class="flex justify-between items-center border-b px-4 py-2"
      :class="mode === 'dark' ? 'border-dark-50' : ''"
    >
      <div class="flex items-center gap-2">
        <button class="button primary-button" @click="copyAllResultToClipboard">
          <Clipboard :size="20" :stroke-width="1.75" />
          Copy all results to clipboard
        </button>
        <button
          class="button bg-danger-100 hover:brightness-125"
          @click="emit('clear')"
        >
          <Eraser :size="20" :stroke-width="1.75" />
          Clear
        </button>
      </div>
      <div
        class="flex items-center gap-2"
        v-if="
          tab === 'Topical Cluster' && result[30]?.topical_cluster_emqs.length
        "
      >
        <button
          v-if="selectedEmqs.length"
          class="button primary-button"
          @click="handleShowBulkSearch"
        >
          <SendHorizonal :size="20" :stroke-width="1.75" />
          Run Bulk Keyword Spy Reports
        </button>
        <button
          v-if="selectedEmqs.length"
          class="button primary-button"
          @click="handleBulkSearchAndAutoAiArticle"
        >
          <SendHorizonal :size="20" :stroke-width="1.75" />
          Run Reports Generate Auto AI Article
        </button>
        <button
          class="button primary-button"
          :disabled="!selectedEmqs.length"
          @click="copySelectedToClipboard"
        >
          <Clipboard :size="20" :stroke-width="1.75" />
          Copy selected to clipboard
        </button>
      </div>
    </div>
    <div class="grid gap-4 p-4">
      <template v-for="(value, key) in result" :key="key">
        <div>
          <div
            class="bg-success-100 text-success-100 text-[15px] rounded-lg p-4 bg-opacity-20 mb-4"
            v-if="!['14', '17'].includes(key.toString())"
          >
            <span>{{ prompts[key]["title"] || "" }}</span>
          </div>
          <div class="text-[15px] font-medium mb-2">
            Total AI credit used for this prompt:
            {{ value.total_tokens_used }}
          </div>
          <div class="grid gap-4">
            <div
              :class="[{ 'grid grid-cols-2 gap-4': key.toString() === '30' }]"
              v-for="(item, index) in value.result"
              :key="index"
            >
              <div
                class="flex-1 border rounded-lg"
                :class="[mode === 'dark' ? 'bg-dark-70 border-dark-50' : '']"
              >
                <h6
                  class="border-b px-4 py-3 font-semibold text-[17px]"
                  :class="mode === 'dark' ? 'border-dark-50' : ''"
                  v-if="key.toString() === '30'"
                >
                  Topical Cluster Hierarchy / Knowledge Graph Entities
                </h6>
                <div
                  class="break-words text-[15px]"
                  :class="[
                    key.toString() === '30'
                      ? 'whitespace-pre-wrap px-4 pt-4'
                      : 'px-4 pt-4',
                  ]"
                  v-html="formatHTML(item)"
                ></div>
                <div
                  v-if="!['30'].includes(key.toString())"
                  :class="[!['30'].includes(key.toString()) ? 'px-4 pb-4' : '']"
                  class="mt-2"
                >
                  <div
                    class="button small-button primary-button"
                    @click="copyResultToClipboard(item, key)"
                  >
                    <Clipboard :size="20" :stroke-width="1.75" />
                    Copy to clipboard
                  </div>
                </div>
              </div>
              <div
                class="flex-1 border rounded-lg"
                :class="[mode === 'dark' ? 'bg-dark-70 border-dark-50' : '']"
                v-if="key.toString() === '30' && value.topical_cluster_emqs"
              >
                <h6
                  class="border-b px-4 py-3 font-semibold text-[17px]"
                  :class="mode === 'dark' ? 'border-dark-50' : ''"
                  v-if="value.topical_cluster_emqs.length"
                >
                  Exact Match Queries / Topical Cluster Target Keywords
                </h6>
                <div class="p-4 grid gap-4">
                  <div
                    class="checkbox"
                    v-for="(emq, index) in value.topical_cluster_emqs"
                    :key="`cluster-emq-${index}`"
                  >
                    <input
                      type="checkbox"
                      :value="emq"
                      :id="`cluster-emq-${index}`"
                      v-model="selectedEmqs"
                      v-tooltip="{
                        content: `<div style='max-width: 300px;'>(FEATURE building in progress) Highlight the desired queries below to run a report on later in BULK and click COPY</div>`,
                        html: true,
                      }"
                    />
                    <label :for="`cluster-emq-${index}`">{{ emq }}</label>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div
          v-if="!['14', '17', '25', '30'].includes(key.toString())"
          class="rounded-lg border"
          :class="mode === 'dark' ? 'border-dark-50' : ''"
        >
          <div
            class="border-b px-4 py-3 font-semibold text-[17px]"
            :class="mode === 'dark' ? 'border-dark-50' : ''"
          >
            Drill down
          </div>
          <ul class="grid gap-2 p-4">
            <li
              class="p-3 border rounded-lg flex items-center gap-2"
              @click="emit('handleDropdownSelect', key, 1)"
              :class="[
                drillDownActiveClass(key, 1),
                mode === 'dark' ? 'border-dark-50' : '',
              ]"
            >
              Tell me more about this
            </li>
            <li
              class="p-3 border rounded-lg flex items-center gap-2"
              @click="emit('handleDropdownSelect', key, 2)"
              :class="[
                drillDownActiveClass(key, 2),
                mode === 'dark' ? 'border-dark-50' : '',
              ]"
            >
              <span>Tell me more about</span>
              <span v-if="!drillDownActive(key, 2)">__</span>
              <input
                v-if="drillDownActive(key, 2)"
                type="text"
                class="form-field w-auto py-0 h-8"
                :class="mode === 'dark' ? '' : 'text-black-100'"
                :value="selectedDropdown[key].customText"
                @change="emit('customDropdownText', key, 2, $event)"
                placeholder="Custom text..."
              />
            </li>
            <li
              class="p-3 border rounded-lg flex items-center gap-2"
              @click="emit('handleDropdownSelect', key, 3)"
              :class="[
                drillDownActiveClass(key, 3),
                mode === 'dark' ? 'border-dark-50' : '',
              ]"
            >
              <span>Explain how</span>
              <span v-if="!drillDownActive(key, 3)">____</span>
              <input
                v-if="drillDownActive(key, 3)"
                type="text"
                class="form-field w-auto py-0 h-8"
                :class="mode === 'dark' ? '' : 'text-black-100'"
                :value="selectedDropdown[key].customText"
                @change="emit('customDropdownText', key, 3, $event)"
                placeholder="Custom text..."
              />
              <span>works</span>
            </li>
            <li
              class="p-3 border rounded-lg flex items-center gap-2"
              @click="emit('handleDropdownSelect', key, 4)"
              :class="[
                drillDownActiveClass(key, 4),
                mode === 'dark' ? 'border-dark-50' : '',
              ]"
            >
              What are more interesting tips, info and helpful comments on this
            </li>
            <li
              class="p-3 border rounded-lg flex items-center gap-2"
              @click="emit('handleDropdownSelect', key, 5)"
              :class="[
                drillDownActiveClass(key, 5),
                mode === 'dark' ? 'border-dark-50' : '',
              ]"
            >
              What do lay people say about this topic as compared to what the
              experts say
            </li>
          </ul>
          <div v-if="drillDownSelectedForPrompt(key)" class="px-4 pb-4">
            <button
              class="button primary-button"
              @click="emit('loadResults', key)"
            >
              <CheckCheck :size="20" :stroke-width="1.75" />
              Submit
            </button>
          </div>
        </div>
      </template>
    </div>
    <div
      class="bg-opacity-40 fixed inset-0 z-[9999] flex items-center justify-center p-4"
      :class="mode === 'dark' ? 'bg-white-100' : 'bg-black-100'"
      v-if="showBulkSearchModal"
    >
      <div
        class="max-w-2xl rounded-xl w-full mx-auto"
        :class="mode === 'dark' ? 'bg-dark-70' : 'bg-white-100'"
      >
        <div
          class="flex items-center justify-between p-4 border-b"
          :class="mode === 'dark' ? 'border-dark-50' : ''"
        >
          <h5 class="font-medium text-[17px] leading-none">Keyword spy</h5>
          <X
            :size="22"
            :stroke-width="1.75"
            @click="showBulkSearchModal = false"
            class="cursor-pointer"
          />
        </div>
        <div
          class="without-scrollbar max-h-modal overflow-y-auto p-4 rounded-bl-xl rounded-br-xl"
          :class="mode === 'dark' ? 'bg-dark-70' : 'bg-gray-20 bg-opacity-30'"
        >
          <div class="grid grid-cols-2 gap-4">
            <div class="flex-1">
              <Multiselect
                v-model="location"
                placeholder="Search by typing your location"
                label="full_name"
                valueProp="full_name"
                @change="handleLocationChange"
                @open="handleOpenOptionList"
                :delay="0"
                :searchable="true"
                :options="
                  async function (searchQuery) {
                    if (!searchQuery) return await fetchLocations('a');
                    return await fetchLocations(searchQuery);
                  }
                "
                object
                class="form-field"
              >
              </Multiselect>
            </div>
            <div class="flex-1">
              <Multiselect
                v-model="googleDomain"
                :options="domainDetailsList"
                placeholder="Choose google domain"
                :searchable="true"
                :canDeselect="true"
                label="domain"
                valueProp="domain"
                class="form-field"
              ></Multiselect>
            </div>
            <div class="flex-1">
              <Multiselect
                class="form-field"
                v-model="googleCountry"
                :options="countries"
                placeholder="Choose country"
                :searchable="true"
                :canDeselect="true"
              ></Multiselect>
            </div>
            <div class="flex-1">
              <Multiselect
                class="form-field"
                v-model="googleLanguage"
                :options="languages"
                placeholder="Choose language"
                :searchable="true"
                :canDeselect="true"
              ></Multiselect>
            </div>
            <div class="flex-1">
              <div
                class="h-11 border rounded-md flex items-center px-3"
                :class="
                  mode === 'dark'
                    ? 'border-dark-50 bg-dark-70'
                    : 'border-gray-30 border-opacity-50 bg-white-100'
                "
              >
                <div class="checkbox">
                  <input
                    id="track-position-ranking"
                    type="checkbox"
                    v-model="trackPositionRanking"
                  />
                  <label for="track-position-ranking"
                    >Track position rankings</label
                  >
                </div>
              </div>
            </div>
            <div class="flex-1">
              <Multiselect
                class="form-field"
                v-model="selectedProject"
                :options="projects"
                placeholder="Choose project"
                label="name"
                valueProp="uuid"
                :searchable="true"
                :canDeselect="true"
              ></Multiselect>
            </div>
            <div class="col-span-2 flex-1" v-if="trackPositionRanking">
              <input
                class="form-field"
                type="text"
                v-model="trackPositionURL"
                placeholder="Enter URL"
              />
            </div>
            <div
              class="col-span-2 border rounded-md"
              :class="
                mode === 'dark'
                  ? 'border-dark-50'
                  : 'bg-white-100 border-gray-30 border-opacity-50'
              "
            >
              <div
                class="p-4 border-b"
                :class="
                  mode === 'dark'
                    ? 'border-dark-50'
                    : 'border-gray-30 border-opacity-50'
                "
              >
                <h6 class="font-medium leading-none">
                  AI models/testing options
                </h6>
              </div>
              <div class="grid grid-cols-2 gap-4 p-4">
                <template v-for="(test, index) in performTest" :key="index">
                  <div
                    class="h-11 border rounded-md items-center px-3"
                    :class="[
                      mode === 'dark'
                        ? 'border-dark-50 bg-dark-70'
                        : 'border-gray-30 border-opacity-50 bg-gray-20 bg-opacity-10',
                      test.label === 'Neural Matching Test' ? 'hidden' : 'flex',
                    ]"
                  >
                    <div class="checkbox">
                      <input
                        :disabled="test.disable"
                        type="checkbox"
                        :id="test.key"
                        :checked="test.value"
                        :value="test.value"
                        v-model="test.value"
                      />
                      <label :for="test.key">{{ test.label }}</label>
                    </div>
                  </div>
                </template>
              </div>
            </div>
          </div>
        </div>
        <div
          class="flex items-center justify-end p-4 border-t space-x-2"
          :class="mode === 'dark' ? 'border-dark-50' : ''"
        >
          <button class="button primary-button" @click="handleSearchBulkSearch">
            <CheckCheck :size="20" :stroke-width="1.75" />
            {{ isAutoGenerateArticle ? "Next" : "Submit" }}
          </button>
        </div>
      </div>
    </div>
    <div
      class="bg-opacity-40 fixed inset-0 z-[9999] flex items-center justify-center p-4"
      :class="mode === 'dark' ? 'bg-white-100' : 'bg-black-100'"
      v-if="isAutoGenerateArticleModal"
    >
      <div
        class="max-w-3xl rounded-xl w-full mx-auto"
        :class="mode === 'dark' ? 'bg-dark-70' : 'bg-white-100'"
      >
        <div
          class="flex items-center justify-between p-4 border-b"
          :class="mode === 'dark' ? 'border-dark-50' : ''"
        >
          <h5 class="font-medium text-[17px] leading-none">
            Get keyword spy to generate your optimized expert article
          </h5>
          <X
            :size="22"
            :stroke-width="1.75"
            @click="handleCloseModals"
            class="cursor-pointer"
          />
        </div>
        <div
          class="without-scrollbar max-h-modal overflow-y-auto p-4 rounded-bl-xl rounded-br-xl"
          :class="mode === 'dark' ? 'bg-dark-70' : 'bg-gray-20 bg-opacity-30'"
        >
          <div class="grid grid-cols-2 gap-4">
            <div>
              <label for="author" class="text-[15px] block mb-1"
                >Author & Role</label
              >
              <input
                type="text"
                v-model="authorAndRole"
                placeholder="e.g. brand.com or John Smith, expert EMQ/niche blogger"
                class="form-field"
                id="author"
              />
              <p
                v-if="showAuthorError && !authorAndRole"
                class="block mt-1 text-red-100 text-sm"
              >
                Please enter author & role (manditory)
              </p>
            </div>
            <div>
              <label for="gen-sales" class="text-[15px] block mb-1"
                >Sales cycle</label
              >
              <Multiselect
                id="gen-sales"
                v-model="metaDescriptonSales"
                placeholder="Select a sales cycle"
                :options="saleOptions"
                class="form-field"
              >
              </Multiselect>
              <p
                v-if="showSalesError && !metaDescriptonSales"
                class="block mt-1 text-red-100 text-sm"
              >
                Select a sales cycle in dropdown (manditory)
              </p>
            </div>
            <div
              class="h-11 border rounded-md flex items-center px-4"
              :class="
                mode === 'dark'
                  ? 'bg-dark-70 border-dark-50'
                  : 'border-gray-30 border-opacity-50 bg-white-100'
              "
            >
              <div class="checkbox">
                <input
                  type="checkbox"
                  value="url"
                  id="genURL"
                  v-model="articleSelectedPrompts"
                />
                <label for="genURL">URL</label>
              </div>
            </div>
            <div
              class="h-11 border rounded-md flex items-center px-4"
              :class="
                mode === 'dark'
                  ? 'bg-dark-70 border-dark-50'
                  : 'border-gray-30 border-opacity-50 bg-white-100'
              "
            >
              <div class="checkbox">
                <input
                  type="checkbox"
                  value="title"
                  id="genTitle"
                  v-model="articleSelectedPrompts"
                />
                <label for="genTitle">Title</label>
              </div>
            </div>
            <div
              class="h-11 border rounded-md flex items-center px-4"
              :class="
                mode === 'dark'
                  ? 'bg-dark-70 border-dark-50'
                  : 'border-gray-30 border-opacity-50 bg-white-100'
              "
            >
              <div class="checkbox">
                <input
                  type="checkbox"
                  value="md"
                  id="genMD"
                  v-model="articleSelectedPrompts"
                />
                <label for="genMD">Meta description</label>
              </div>
            </div>
            <div
              class="h-11 border rounded-md flex items-center px-4"
              :class="
                mode === 'dark'
                  ? 'bg-dark-70 border-dark-50'
                  : 'border-gray-30 border-opacity-50 bg-white-100'
              "
            >
              <div class="checkbox">
                <input
                  type="checkbox"
                  value="hl1"
                  id="genhl1"
                  v-model="articleSelectedPrompts"
                />
                <label for="genhl1">
                  Heading level 1 (title of article in content)
                </label>
              </div>
            </div>
            <template v-if="articleSelectedPrompts.includes('md')">
              <div>
                <label for="gen-angle" class="text-[15px] block mb-1"
                  >Angle</label
                >
                <input
                  type="text"
                  class="form-field"
                  id="gen-angle"
                  placeholder="Leave blank to let AI decide OR enter angle eg. sale on, info, guide, review, ecommerce, etc"
                  v-model="metaDescriptonAngle"
                />
              </div>
              <div>
                <label for="gen-perks" class="text-[15px] block mb-1"
                  >Perks</label
                >
                <input
                  type="text"
                  class="form-field"
                  id="gen-perks"
                  placeholder="Leave blank to let AI decide OR enter perks e.g. 25% off, free parking, buy one get one free, etc"
                  v-model="metaDescriptonPerks"
                />
              </div>
            </template>
            <div class="col-span-2">
              <div
                class="rounded-lg px-4 py-2 flex gap-2"
                :class="mode === 'dark' ? 'bg-warning-100' : 'bg-warning-50'"
              >
                <p class="text-[15px] text-opacity-80 text-black-100">
                  Please be aware that by proceeding with the upcoming action,
                  your current content in the Article Builder will be
                  permanently replaced.
                </p>
              </div>
            </div>
          </div>
        </div>
        <div
          class="flex items-center justify-end p-4 border-t space-x-2"
          :class="mode === 'dark' ? 'border-dark-50' : ''"
        >
          <button class="button primary-button" @click="handleBack">
            <StepBack :size="20" :stroke-width="1.75" />
            Back
          </button>
          <button class="button primary-button" @click="handleSubmit">
            <CheckCheck :size="20" :stroke-width="1.75" />
            Submit
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from "vue";
import type { Ref } from "vue";
import { useToast } from "vue-toast-notification";
import helper from "../../libs/helper";
import { isEmpty } from "lodash";
import { useUserStore } from "../../stores/user";
import ApiRequest from "../../libs/ApiRequest";
import Multiselect from "@vueform/multiselect";
import { CreateSearch, PostSearchData } from "../../libs/types";
import { useRouter } from "vue-router";
import {
  CheckCheck,
  Clipboard,
  Eraser,
  SendHorizonal,
  StepBack,
  X,
} from "lucide-vue-next";
interface Props {
  selectedDropdown: any;
  result: any;
  aiPrompts: any;
  selectedAIPrompt: any;
  prompts: any;
  topicNamingSpot: any;
  tab: string;
  mode: string;
}
const props = withDefaults(defineProps<Props>(), {});
const emit = defineEmits([
  "loadResults",
  "handleDropdownSelect",
  "customDropdownText",
  "clear",
  "submit",
]);
const $toast = useToast();
const selectedEmqs = ref([]);
const userStore = useUserStore();
const router = useRouter();
const showBulkSearchModal = ref(false);
const location = ref(null);
const limit = ref(100);
const searchType = ref(0);
const stopwords = ref([]);
const showStopwords = ref(true);
const domainDetails = ref([]);
const googleCountry = ref("");
const googleDomain = ref("");
const googleLanguage = ref("");
const countries = ref([]);
const domainDetailsList = ref([]);
const languages = ref([]);
const trackPositionRanking = ref(false);
const trackPositionURL = ref("");
const selectedProject = ref(null);
const projects = ref([]);
const selectedSpeedThreshold = ref(0);
const isAutoGenerateArticle = ref(false);
const isAutoGenerateArticleModal = ref(false);
const articleSelectedPrompts: Ref<string[]> = ref([]);
const articleType = ref("high_quality");
const authorAndRole = ref(null);
const showAuthorError = ref(false);
const showSalesError = ref(false);
const metaDescriptonAngle = ref("");
const metaDescriptonPerks = ref("");
const metaDescriptonSales = ref("Middle of Funnel");
const saleOptions = ref([
  {
    label: "Top",
    value: "Top of Funnel",
  },
  {
    label: "Middle",
    value: "Middle of Funnel",
  },
  {
    label: "Bottom",
    value: "Bottom of Funnel",
  },
]);
const performTest = ref([
  {
    label: "BERT test",
    value: true,
    key: "bert_test1",
    disable: true,
  },
  {
    label: "RankBrain test",
    value: true,
    key: "perform_rankbrain_test",
    disable: false,
  },
  {
    label: "TW-BERT test",
    value: true,
    key: "perform_tw_bert_test",
    disable: false,
  },
  {
    label: "Query interpretation test",
    value: true,
    key: "perform_query_interpt_test",
    disable: false,
  },
]);
onMounted(async () => {
  searchType.value = helper.searchTypes.TokenAnalysis;
  showStopwords.value && loadStopWords();
  loadSupportedInfo();
  const { data } = await ApiRequest().get(`/api/serp/projects`);
  const { results = {} } = data;
  projects.value = results;
});
const handleShowBulkSearch = () => {
  showBulkSearchModal.value = true;
  isAutoGenerateArticle.value = false;
};
const handleBulkSearchAndAutoAiArticle = () => {
  showBulkSearchModal.value = true;
  isAutoGenerateArticle.value = true;
};
const handleSubmit = () => {
  if (!metaDescriptonSales.value) {
    showSalesError.value = true;
    return;
  }
  if (!authorAndRole.value) {
    showAuthorError.value = true;
    return;
  }
  createReports();
};
const loadSupportedInfo = async () => {
  const {
    extractedError,
    countries: apiCountries,
    languages: apiLanguages,
    domain_details,
  } = await userStore.getSupportedInfo();
  if (extractedError) {
    userStore.setIsLoading(!1);
    $toast.error(extractedError.alert.message);
    if (extractedError.redirect) {
      userStore.signOut();
      router.push({ name: extractedError.redirect });
    }
  }
  countries.value = apiCountries;
  languages.value = apiLanguages;
  domainDetails.value = domain_details;
  domainDetailsList.value = domainDetails.value
    ? domainDetails.value.filter((obj, index, arr) => {
        return (
          arr.findIndex((innerObj) => innerObj.domain === obj.domain) === index
        );
      })
    : [];
  getDomainDetailsList("", domainDetailsList.value);
};
const fetchLocations = async (searchQuery) => {
  try {
    const { data } = await ApiRequest().get(
      `https://api.valueserp.com/locations?api_key=demo&q=${searchQuery}`
    );
    let locations = data && data.locations ? data.locations : [];
    if (locations.length) {
      locations = locations.map((l) => {
        return { ...l, full_name: l.full_name.split(",").join(", ") };
      });
    }
    return locations;
  } catch (err) {
    console.error("get locations error", err);
  }
};
const handleOpenOptionList = () => {
  location.value = null;
};
const handleLocationChange = (value) => {
  if (value && !isEmpty(value)) {
    const google_domain = domainDetails.value.find(
      (i) => i.main_country.toLowerCase() === value.country_code.toLowerCase()
    );
    googleCountry.value =
      (google_domain && google_domain.country_code) || value.country_code;
    googleDomain.value = (google_domain && google_domain.domain) || "";
    googleLanguage.value = (google_domain && google_domain.language_code) || "";
  } else {
    googleCountry.value = "";
    googleDomain.value = "";
    googleLanguage.value = "";
  }
};
const loadStopWords = async () => {
  if (stopwords.value.length > 0) {
    return stopwords.value;
  }
  try {
    const URL = `/api/serp/search/stopwords`;
    const res = await ApiRequest().get(URL);
    if (res.status !== 200) {
      throw new Error("Something went wrong");
    }
    stopwords.value = res.data?.stopwords;
  } catch (error) {
    userStore.setIsLoading(!1);
    const extractedError = helper.extractAlert(error);
    if (extractedError.redirect) {
      userStore.signOut();
      router.push({ name: extractedError.redirect });
    }
  }
};
const getDomainDetailsList = (q, options) => {
  let lists = options;
  if (lists) {
    const matchedEntry = lists.find((list) => list.domain === "google.com");
    if (matchedEntry) {
      lists = [matchedEntry, ...lists.filter((d) => d.domain !== "google.com")];
    }
    domainDetailsList.value = lists;
  }
};
const handleBack = () => {
  showBulkSearchModal.value = true;
  isAutoGenerateArticleModal.value = false;
};
const handleCloseModals = () => {
  showBulkSearchModal.value = false;
  isAutoGenerateArticleModal.value = false;
};
const handleSearchBulkSearch = async () => {
  if (!location.value) {
    $toast.error("Please select a location");
    return;
  }
  if (!selectedEmqs.value.length) {
    $toast.error("Please enter bulk search query");
    return;
  }
  if (selectedEmqs.value.length > 10) {
    $toast.error("Max. 10 Keyword/Exact Match Queries are allowed");
    return;
  }
  if (trackPositionRanking.value && !trackPositionURL.value) {
    $toast.error("Please enter a URL to track position rankings");
    return;
  }
  if (isAutoGenerateArticle.value) {
    showBulkSearchModal.value = false;
    isAutoGenerateArticleModal.value = true;
    return;
  }
  createReports();
};
const createReports = async () => {
  const payload = {};
  performTest.value.forEach((test) => {
    payload[test.key] = test.value;
  });
  try {
    userStore.setIsLoading(!0);
    const postData: CreateSearch[] = [];
    for (const query of selectedEmqs.value) {
      const newSearch: CreateSearch = {
        ...payload,
        query: query,
        limit: limit.value,
        type: searchType.value,
        speed_threshold: selectedSpeedThreshold.value,
        google_domain: googleDomain.value,
        location: location.value ? location.value.full_name : "",
        country: googleCountry.value,
        language: googleLanguage.value,
        track_url_status:
          trackPositionRanking.value && trackPositionURL.value ? 20 : 10,
        ...(trackPositionRanking.value
          ? { track_url: trackPositionURL.value }
          : {}),
        ...(selectedProject.value
          ? { project_uuid: selectedProject.value }
          : {}),
        ...(isAutoGenerateArticle.value
          ? {
              perform_actions: {
                meta_description: articleSelectedPrompts.value.includes("md"),
                title: articleSelectedPrompts.value.includes("title"),
                h1: articleSelectedPrompts.value.includes("hl1"),
                article: true,
                url: articleSelectedPrompts.value.includes("url"),
                angle: metaDescriptonAngle.value,
                perks: metaDescriptonPerks.value,
                sales: metaDescriptonSales.value,
                auther_role: authorAndRole.value,
                article_type: articleType.value,
              },
            }
          : {}),
      };
      postData.push(newSearch);
    }
    const { status } = await ApiRequest().post<PostSearchData[]>(
      `/api/serp/search/spy_bulk_search`,
      postData
    );
    if (status == 201) {
      router.push({ name: "reports" });
    }
  } catch (error) {
    const extractedError = helper.extractAlert(error);
    emit("submit", extractedError, null);
    if (extractedError) {
      userStore.setIsLoading(!1);
      $toast.error(extractedError.alert.message);
      if (extractedError.redirect) {
        userStore.signOut();
        router.push({ name: extractedError.redirect });
      }
    }
  } finally {
    userStore.setIsLoading(!1);
  }
};
const drillDownSelectedForPrompt = (promptID: any) => {
  return Boolean(props.selectedDropdown[promptID] || false);
};
const drillDownActive = (promptID: any, drillDownIndex: any) => {
  const item = props.selectedDropdown[promptID] || {};
  return item.index === drillDownIndex;
};
const drillDownActiveClass = (promptID: any, drillDownIndex: any) => {
  return drillDownActive(promptID, drillDownIndex)
    ? "bg-primary-100 text-white-100"
    : "";
};
const formatHTML = (text: any) => {
  return text ? text.replaceAll("\n", "<br />") : "";
};
const copyResultToClipboard = async (resultObj: any, key: any) => {
  let text = resultObj;
  if (key === "25") {
    const parts = resultObj.split(":");
    text = parts[parts.length - 1];
  }
  const { alertType, message } = await helper.copyToClipboard(text);
  if (alertType === "error") {
    $toast.error(message);
  } else {
    $toast.success(message);
  }
};
const copyAllResultToClipboard = () => {
  let text: Array<any> = [];
  for (const key of Object.keys(props.result)) {
    if (key.toString() === "30") {
      text = [...text, props.result[key]["topical_cluster_emqs"]];
    } else {
      const result: Array<any> = props.result[key].result;
      for (let i = 0; i < result.length; i++) {
        if (key === "25") {
          const parts = result[i].split(":");
          text.push(parts[parts.length - 1]);
        } else {
          text.push(result[i]);
        }
      }
    }
  }
  if (props.result[30]) {
    copyResultToClipboard(
      props.result[30]["topical_cluster_emqs"].join("\n"),
      "30"
    );
  } else {
    copyResultToClipboard(text.join("\r\n\r\n"), "");
  }
};
const copySelectedToClipboard = () => {
  copyResultToClipboard(selectedEmqs.value.join("\n"), "30");
};
</script>
